// S37: the toolbar's script, one toolbar for every table since P5 (D98, plan 4.3): the search, the
// Filters button's panel (position with its custom range, search intent and topic), a card table's Sort,
// Clear filters, sorting and the count line. A sort or a filter changed starts the list again from its
// first page (pager.js).

const NOT_SORTED = "not_sorted";

export default function toolbarPart({ TYPING_ANNOUNCE_MS, roundedPosition, foldText }) {
    return {
        // A card table (ActivityTable::LAYOUT_CARD) takes S37's toolbar; every other table keeps today's.
        isCard() {
            return this.table.layout === "card";
        },

        // A finished row folded in place (a notice, D70) is no longer to do.
        isFolded(row) {
            return Boolean(this.notices[row.id]);
        },

        isMatched(row) {
            // On a card table a folded row keeps its place under any filter, so its Undo is never
            // filtered out of reach.
            if (this.isCard() && this.isFolded(row)) {
                return true;
            }

            if (this.positionFrom !== null) {
                const column = this.positionColumn();
                const cell = column ? row.cells[column.key] : null;

                if (!cell || cell.value === null || cell.value === undefined) {
                    return false;
                }

                const position = roundedPosition(cell.value);

                if (position < this.positionFrom || position > this.positionTo) {
                    return false;
                }
            }

            if (this.stagesPicked.length > 0 && !this.stagesPicked.includes(row.stage ?? NOT_SORTED)) {
                return false;
            }

            if (this.topicsPicked.length > 0 && (row.topicId === null || !this.topicsPicked.includes(String(row.topicId)))) {
                return false;
            }

            const needle = foldText(this.query);

            if (needle === "") {
                return true;
            }

            return this.table.columns.some((column) => {
                const display = column.kind === "text" ? row.cells[column.key].display : null;

                return typeof display === "string" && foldText(display).includes(needle);
            });
        },

        hasFilters() {
            return this.positionFrom !== null || this.stagesPicked.length > 0 || this.topicsPicked.length > 0 || foldText(this.query) !== "";
        },

        // How many picks the Filters panel holds, said on its button: a position range is one, and each
        // search intent and each topic ticked is one, so the figure is the ticks the panel shows. The
        // search box beside it is not counted: what it holds is on screen.
        filterCount() {
            return (this.positionFrom !== null ? 1 : 0) + this.stagesPicked.length + this.topicsPicked.length;
        },

        // The button's hidden words after its figure ("2 picked"), so a screen reader hears what it counts.
        filterCountLabel() {
            return this.strings.picked.replace(":count", this.filterCount());
        },

        // Any filter or the search changed: the list starts again from its first page, and its count is said.
        filtersChanged(isTyping = false) {
            this.restartPages();
            this.refresh();
            this.announce(this.summary(), isTyping ? TYPING_ANNOUNCE_MS : 0);
        },

        // B15: the one test of what the list counts, read by the count line and the pager alike (pager.js),
        // so the two never disagree. On a card table that is the rows still to do: a folded row (Done or
        // Not relevant, its Undo in place) keeps its place in the list but is done. Any other table counts
        // every row, a notice too, since a row leaves it only when its notice is closed.
        isCounted(row) {
            return !(this.isCard() && this.isFolded(row));
        },

        // The rows the list counts, and those of them the filters keep: on a card table the rows still to
        // do, which "All caught up" and "No searches match" read as well.
        toDoCount() {
            return this.rows.filter((row) => this.isCounted(row)).length;
        },

        matchedToDoCount() {
            return this.matched.filter((row) => this.isCounted(row)).length;
        },

        summary() {
            if (this.isCard()) {
                return this.cardSummary();
            }

            const count = this.toDoCount();

            // No paging note: the pager under the list says which rows its page holds.
            return this.hasFilters()
                ? this.strings.matching.replace(":matched", this.matchedToDoCount()).replace(":count", count)
                : (count === 1 ? this.strings.rowsOne : this.strings.rowsMany).replace(":count", count);
        },

        // "{n} searches", or "Showing {x} of {y} searches" while a filter is on: rows still to do only.
        cardSummary() {
            const count = this.toDoCount();
            const words = this.strings.card;

            return this.hasFilters()
                ? words.showing.replace(":shown", this.matchedToDoCount()).replace(":count", count)
                : (count === 1 ? words.rows_one : words.rows_many).replace(":count", count);
        },

        // ---------------------------------------------------------------------------------- sorting

        sortBy(key) {
            const column = this.columnOf(key);

            if (!column || !column.sortable) {
                return;
            }

            if (this.sortKey === key) {
                this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
            } else {
                this.sortKey = key;
                this.sortDirection = column.firstSort;
            }

            this.restartPages();
            this.refresh();
            this.announce(this.strings.sorted.replace(":column", column.label).replace(":direction", this.directionLabel(column, this.sortDirection)));
        },

        // A card table's Sort: the columns it offers (the table's sortChoices), and a pick, which sorts by
        // that column its best way first. Picking the sort that is on keeps it, never flips it: a pick-one
        // says what it is, unlike a header's press.
        sortChoices() {
            return this.table.sortChoices.map((key) => this.columnOf(key)).filter(Boolean);
        },

        sortTo(key) {
            const column = this.columnOf(key);

            if (!column || !column.sortable) {
                return;
            }

            this.sortKey = key;
            this.sortDirection = column.firstSort;
            this.restartPages();
            this.refresh();
            this.announce(this.strings.sorted.replace(":column", column.label).replace(":direction", this.directionLabel(column, this.sortDirection)));
            this.closePanel(true);
        },

        sortSummary() {
            const column = this.sortKey === null ? null : this.columnOf(this.sortKey);

            return this.strings.card.sort.replace(":choice", column ? column.label : "…");
        },

        // Whether the rows are in the order the table arrived in (its defaultSort), the one its orderNote
        // describes (P21): once Sort picks another, "Ranked by Opportunity score" would be untrue.
        isDefaultOrder() {
            const order = this.table.defaultSort;

            return order !== null && this.sortKey === order.column && this.sortDirection === order.direction;
        },

        ariaSortOf(key) {
            if (this.sortKey !== key) {
                return null;
            }

            return this.sortDirection === "asc" ? "ascending" : "descending";
        },

        sortArrowOf(key) {
            if (this.sortKey !== key) {
                return "";
            }

            return this.sortDirection === "asc" ? "↑" : "↓";
        },

        directionLabel(column, direction) {
            const prefix = { text: "text_", position: "position_", stage: "stage_" }[column.kind] || "";

            return this.strings.direction[prefix + direction];
        },

        // ---------------------------------------------------------------------------------- filters

        // null picks any position. The Filters panel's position section is a pick-one: the pick is marked
        // aria-pressed and focus stays on it, and the panel stays open for the other filters. The custom
        // range's fields empty, so they hold a range only while it is the one on.
        pickPosition(key) {
            const preset = this.presets.find((candidate) => candidate.key === key) || null;

            this.positionPreset = preset ? preset.key : null;
            this.positionFrom = preset ? preset.from : null;
            this.positionTo = preset ? preset.to : null;
            this.customFrom = "";
            this.customTo = "";
            this.isCustomInvalid = false;
            this.filtersChanged();
        },

        isPositionPicked(key) {
            return this.positionPreset === key;
        },

        // Whole positions from 1 to the limit, the first no larger than the second: a reader's "2 to 5".
        applyCustomRange() {
            const fromText = String(this.customFrom).trim();
            const toText = String(this.customTo).trim();
            const from = Number(fromText);
            const to = Number(toText);
            const isValid = fromText !== "" && toText !== "" && Number.isInteger(from) && Number.isInteger(to) && from >= 1 && to <= this.positionLimit && from <= to;

            this.isCustomInvalid = !isValid;

            if (!isValid) {
                return false;
            }

            this.positionPreset = "custom";
            this.positionFrom = from;
            this.positionTo = to;
            this.filtersChanged();

            return true;
        },

        stageToken(choice) {
            return choice.value ?? NOT_SORTED;
        },

        isStagePicked(token) {
            return this.stagesPicked.includes(token);
        },

        toggleStage(token) {
            this.stagesPicked = this.isStagePicked(token) ? this.stagesPicked.filter((picked) => picked !== token) : [...this.stagesPicked, token];
            this.filtersChanged();
        },

        isTopicPicked(id) {
            return this.topicsPicked.includes(String(id));
        },

        toggleTopic(id) {
            const token = String(id);

            this.topicsPicked = this.topicsPicked.includes(token) ? this.topicsPicked.filter((picked) => picked !== token) : [...this.topicsPicked, token];
            this.filtersChanged();
        },

        // The Topic section's own search, over topic names, once a site has more than a screenful.
        topicChoices() {
            const needle = foldText(this.topicQuery);

            return needle === "" ? this.table.topics : this.table.topics.filter((topic) => foldText(topic.name).includes(needle));
        },

        setQuery(value) {
            this.query = value;
            this.filtersChanged(true);
        },

        // Every filter and the search back to none, and the list from its first page: Clear filters, and a
        // link to a row they hide (bringForward(), Ali, 7 Oct), which says nothing of its own. The sort is
        // not a filter, and stays.
        resetFilters() {
            this.positionPreset = null;
            this.positionFrom = null;
            this.positionTo = null;
            this.customFrom = "";
            this.customTo = "";
            this.isCustomInvalid = false;
            this.stagesPicked = [];
            this.topicsPicked = [];
            this.topicQuery = "";
            this.query = "";
            this.openPanel = null;
            this.restartPages();
            this.refresh();
        },

        // The button leaves with the filters, so focus goes to the search box rather than the page.
        clearFilters() {
            this.resetFilters();
            this.announce(this.summary());
            this.afterShow(() => {
                const search = this.rootElement.querySelector("[data-search]");

                if (search) {
                    search.focus();
                }
            });
        },

        // --------------------------- a toolbar or pager panel: Filters, Sort, Rows per page (a disclosure)

        // Focus goes in on the pick that is on, else a ticked box, else the first control. Asked one at a
        // time: one selector list would match the first control in the panel, pressed or not. A panel shut
        // again before it showed takes no focus.
        togglePanel(name) {
            this.openPanel = this.openPanel === name ? null : name;

            if (this.openPanel === name) {
                this.afterShow(() => {
                    if (this.openPanel !== name) {
                        return;
                    }

                    const panel = this.rootElement.querySelector(`[data-panel="${name}"]`);
                    const target = panel && (panel.querySelector('[aria-pressed="true"]') || panel.querySelector("input:checked") || panel.querySelector("button, input"));

                    if (target) {
                        target.focus();
                    }
                });
            }
        },

        closePanel(returnFocus) {
            const name = this.openPanel;

            this.openPanel = null;

            if (returnFocus && name !== null) {
                const trigger = this.rootElement.querySelector(`[data-panel-trigger="${name}"]`);

                if (trigger) {
                    trigger.focus();
                }
            }
        },

        // Tab past a panel's last control closes it and carries on through the page: no trap. Only a
        // move to a named element counts: a click on a panel's own words blurs to nothing (relatedTarget
        // null), and a click elsewhere is click.outside's to close.
        leavePanel(event, name) {
            if (this.openPanel === name && event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) {
                this.openPanel = null;
            }
        },
    };
}
